import API from "../../../services/API";
import Formatter from "../../../services/Formatter";
import BroadcastSession from "../../../services/BroadcastSession";
import { responseErrors, downloadExport } from "./broadcastActionHelpers";

/**
 * Announcements & pop-ups engine (Dashboard queue, "All Announcements" tab, archive page,
 * publisher screens). Every call goes through API.call so the JWT and X-Authorization headers
 * are added for free. Spec: PRs/org-broadcasts/SPEC.md sections 6 and 10.2.
 */

// ---------------------------------------------------------------- employee side

// The ordered pop-up queue for this login (tab). A failed fetch is silent: the dashboard
// simply shows no pop-ups rather than an error banner.
export const fetchDueBroadcasts = () => {
    return (dispatch, getState) => {
        // The queue lives in the app layout (SPEC 23.8): wrapped so a call that throws or answers
        // nothing (no API base yet, a stubbed API) ends as BROADCASTS_DUE_FAILED, never a crash.
        let request;
        try {
            request = API.call({
                method: "get",
                url: "/broadcasts/due",
                params: { session_key: BroadcastSession.get() }
            });
        } catch (e) {
            request = Promise.reject(e);
        }
        return Promise.resolve(request)
        .then(result => {
            dispatch({
                'type'  : 'BROADCASTS_DUE_SUCCESS',
                'list'  : result.data.content
            })
        })
        .catch(() => {
            dispatch({
                'type'  : 'BROADCASTS_DUE_FAILED'
            })
        });
    }
}

// The Announcements tab list. `append` is derived from the request: page > 1 adds to what is
// already shown ("Show More"); page 1 (first load, department filter) replaces it.
export const fetchBroadcastFeed = ( params ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/feed",
            params: params
        })
        .then(result => {
            // Server shape (BroadcastFeedController::feed, SPEC 18): {list, departments, page, per_page, has_more}.
            const content = result.data.content || {};
            dispatch({
                'type'        : 'BROADCASTS_FEED_SUCCESS',
                'list'        : content.list,
                'departments' : content.departments,
                'page'        : content.page,
                'has_more'    : content.has_more,
                'append'      : Number(params && params.page) > 1
            })
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            dispatch({
                'type'  : 'BROADCASTS_FEED_FAILED'
            })
        });
    }
}

// One item. A 404 means "not visible to you"; the page shows its own line for that, so no alert.
// The server answers a manager (HR / head) with the MANAGE shape by default (media array, no cta);
// `view = 'audience'` forces the audience shape, which is what the archive page needs so HR sees
// the media and the CTA there too. The edit form passes no view and keeps the manage shape.
export const fetchBroadcast = ( id, view ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/" + id,
            ...(view ? { params: { view: view } } : {})
        })
        .then(result => {
            dispatch({
                'type'  : 'BROADCAST_INSTANCE_SUCCESS',
                'item'  : result.data.content
            })
        })
        .catch(e => {
            const status = e ? e.status : undefined;
            if (status !== 404) {
                dispatch( Formatter.alert_error( e ) )
            }
            dispatch({
                'type'    : 'BROADCAST_INSTANCE_FAILED',
                'status'  : status
            })
        });
    }
}

// Drops the archive page item so a stale one is not shown while the next loads.
export const clearBroadcastInstance = () => {
    return {
        'type'  : 'BROADCAST_INSTANCE_CLEAR'
    }
}

// shown / dismissed / acknowledged / media_watched / completed. Resolves true or false and
// never throws into the UI: the queue keeps the item on screen when the server says no.
export const postBroadcastEvent = ( id, event, extra ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/events",
            data: { event: event, session_key: BroadcastSession.get(), ...(extra || {}) }
        })
        .then(() => true)
        .catch(() => false);
    }
}

export const advanceBroadcastQueue = () => {
    return {
        'type'  : 'BROADCASTS_QUEUE_NEXT'
    }
}

// A built-in form's answers (SPEC 19.4), JSON. Resolves {ok, content | errors | already} and
// never rejects or alerts: FormBody lists the 422 messages itself. A 409 (already answered
// this period) counts as done so the queue moves on.
export const submitBroadcastResponse = ( id, payload ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/responses",
            data: payload
        })
        .then(result => ({ ok: true, content: result.data.content }))
        .catch(e => {
            if (e && e.status === 409) return { ok: true, already: true };
            return { ok: false, errors: responseErrors(e) };
        });
    }
}

// ---------------------------------------------------------------- publisher side

export const fetchManagedBroadcasts = ( scope ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/manage",
            params: { scope: scope }
        })
        .then(result => {
            dispatch({
                'type'  : 'BROADCASTS_MANAGE_SUCCESS',
                'list'  : result.data.content
            })
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            dispatch({
                'type'  : 'BROADCASTS_MANAGE_FAILED'
            })
        });
    }
}

export const fetchBroadcastLookups = () => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/lookups"
        })
        .then(result => {
            dispatch({
                'type'     : 'BROADCAST_LOOKUPS_SUCCESS',
                'lookups'  : result.data.content
            })
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
        });
    }
}

// Shared by create / update / media upload: the form shows a 422 on its own, so no alert for
// validation failures; every other failure raises the standard alert. Always rethrows.
const saveFailed = (dispatch) => (e) => {
    if (!e || e.status !== 422) {
        dispatch( Formatter.alert_error( e ) )
    }
    throw e;
}

const saved = (dispatch, withAlert = true) => (result) => {
    if (withAlert) {
        dispatch( Formatter.alert_success( result, 3000 ));
    }
    dispatch({
        'type'  : 'BROADCAST_SAVED',
        'item'  : result.data.content
    })
    return result;
}

const multipart = { "Content-Type": "multipart/form-data" };

export const createBroadcast = ( data ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts",
            headers: multipart,
            data: data
        })
        .then( saved(dispatch) )
        .catch( saveFailed(dispatch) );
    }
}

// POST (not PUT): PHP does not parse multipart bodies on PUT.
export const updateBroadcast = ( id, data ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/update",
            headers: multipart,
            data: data
        })
        .then( saved(dispatch) )
        .catch( saveFailed(dispatch) );
    }
}

export const setBroadcastStatus = ( id, status ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "put",
            url: "/broadcasts/" + id + "/status",
            data: { status: status }
        })
        .then( saved(dispatch) )
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
        });
    }
}

export const deleteBroadcast = ( id ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "delete",
            url: "/broadcasts/" + id
        })
        .then(result => {
            dispatch( Formatter.alert_success( result, 3000 ));
            dispatch({
                'type'  : 'BROADCAST_DELETED',
                'id'    : id
            })
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
        });
    }
}

// The media endpoints answer with the refreshed manage-shape item.
export const uploadBroadcastMedia = ( id, data ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/media",
            headers: multipart,
            data: data
        })
        .then( saved(dispatch, false) )
        .catch( saveFailed(dispatch) );
    }
}

export const deleteBroadcastMedia = ( id, mediaId ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "delete",
            url: "/broadcasts/" + id + "/media/" + mediaId
        })
        .then( saved(dispatch, false) )
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
        });
    }
}

// "View as" check on the edit form. Resolves to {matches, reasons}; nothing is dispatched.
export const previewBroadcast = ( id, ctx ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/" + id + "/preview",
            params: ctx
        })
        .then(result => result.data.content)
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            throw e;
        });
    }
}

// The employee list of a form / custom item (SPEC 6.2): replaces the item's `employee` rules.
// Resolves {matched, unmatched}; the same 422-is-silent rule as create / update.
export const uploadAudienceList = ( id, file ) => {
    return (dispatch, getState) => {
        const fd = new FormData();
        fd.append("file", file);
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/audience-list",
            headers: multipart,
            data: fd
        })
        .then(result => result.data.content)
        .catch( saveFailed(dispatch) );
    }
}

// The responses page (SPEC 19.5). A 403 (not a viewer) or 404 (gone) is the page's own line,
// so no alert for those.
export const fetchBroadcastResponses = ( id, filters ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/" + id + "/responses",
            params: filters
        })
        .then(result => {
            dispatch({
                'type'       : 'BROADCAST_RESPONSES_SUCCESS',
                'responses'  : result.data.content
            })
        })
        .catch(e => {
            const status = e ? e.status : undefined;
            if (status !== 403 && status !== 404) {
                dispatch( Formatter.alert_error( e ) )
            }
            dispatch({
                'type'    : 'BROADCAST_RESPONSES_FAILED',
                'status'  : status
            })
        });
    }
}

// XLSX / CSV export (SPEC 19.6), downloaded through an anchor like the other report exports.
// Resolves true when the file went out, false otherwise; never rejects.
export const exportBroadcastResponses = ( id, filters, format ) => {
    return (dispatch, getState) => {
        return API.export({
            method: "get",
            url: "/broadcasts/" + id + "/responses/export",
            params: { ...(filters || {}), format: format }
        })
        .then(result => {
            downloadExport(result, "broadcast-" + id + "-responses." + format);
            return true;
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            return false;
        });
    }
}

export const fetchBroadcastReach = ( id ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/" + id + "/reach"
        })
        .then(result => {
            dispatch({
                'type'   : 'BROADCAST_REACH_SUCCESS',
                'reach'  : result.data.content
            })
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            dispatch({
                'type'  : 'BROADCAST_REACH_FAILED'
            })
        });
    }
}

// ---------------------------------------------------------------- per-person documents (SPEC 23.4 / 23.7)

// A pdf_url as the server emits it ("/broadcasts/{id}/assignments/{aid}/pdf", API-relative). A full
// URL or an "/api"-prefixed path is cut back to the "/broadcasts/..." part, since API.export adds
// the base URL and the JWT (a plain link would carry no token).
export const apiPath = (url) => {
    const text = String(url || "");
    const at = text.indexOf("/broadcasts/");
    return at >= 0 ? text.slice(at) : text;
}

// GET /broadcasts/{id}/assignments (23.4). Resolves the content; rejects {status, messages} so the
// page shows its own line (403 / 404 / 422 "not per-person"); other failures also raise the alert.
export const fetchBroadcastAssignments = ( id, query ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "get",
            url: "/broadcasts/" + id + "/assignments",
            params: query
        })
        .then(result => result.data.content)
        .catch(e => {
            const status = e ? e.status : undefined;
            if (status !== 403 && status !== 404 && status !== 422) {
                dispatch( Formatter.alert_error( e ) )
            }
            return Promise.reject({ status: status, messages: responseErrors(e) });
        });
    }
}

// POST /broadcasts/{id}/assignments {user_id, form_version, values} (23.4). Resolves {ok, content}
// or {ok: false, errors}; never rejects (the modal lists the 422 / 403 messages itself).
export const raiseBroadcastAssignment = ( id, body ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "post",
            url: "/broadcasts/" + id + "/assignments",
            data: body
        })
        .then(result => {
            dispatch( Formatter.alert_success( result, 3000 ));
            return { ok: true, content: result.data.content };
        })
        .catch(e => ({ ok: false, errors: responseErrors(e) }));
    }
}

// DELETE /broadcasts/{id}/assignments/{aid} (23.4): pending only (a signed one answers 409).
// Resolves true / false; a refusal raises the standard alert.
export const withdrawBroadcastAssignment = ( id, assignmentId ) => {
    return (dispatch, getState) => {
        return API.call({
            method: "delete",
            url: "/broadcasts/" + id + "/assignments/" + assignmentId
        })
        .then(result => {
            dispatch( Formatter.alert_success( result, 3000 ));
            return true;
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            return false;
        });
    }
}

// GET /broadcasts/{id}/assignments/export?format=xlsx&status= (23.4), downloaded like the responses export.
export const exportBroadcastAssignments = ( id, status, format = "xlsx" ) => {
    return (dispatch, getState) => {
        const params = { format: format };
        if (status && status !== "all") params.status = status;
        return API.export({
            method: "get",
            url: "/broadcasts/" + id + "/assignments/export",
            params: params
        })
        .then(result => {
            downloadExport(result, "broadcast-" + id + "-documents." + format);
            return true;
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            return false;
        });
    }
}

// The document copy (23.7) through the authenticated export call.
export const downloadBroadcastPdf = ( pdfUrl, fallbackName ) => {
    return (dispatch, getState) => {
        return API.export({
            method: "get",
            url: apiPath(pdfUrl)
        })
        .then(result => {
            downloadExport(result, fallbackName || "document.pdf");
            return true;
        })
        .catch(e => {
            dispatch( Formatter.alert_error( e ) )
            return false;
        });
    }
}

// GET /broadcasts/lookups/employees?search= (23.4): at least 2 characters, at most 20 rows
// [{id, emp_num, name, country_name, department_name}]. Resolves [] on anything else.
export const searchBroadcastEmployees = ( search ) => {
    return (dispatch, getState) => {
        const text = String(search || "").trim();
        if (text.length < 2) return Promise.resolve([]);
        return API.call({
            method: "get",
            url: "/broadcasts/lookups/employees",
            params: { search: text }
        })
        .then(result => (Array.isArray(result.data.content) ? result.data.content : []))
        .catch(() => []);
    }
}
